import React, { useState, useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { notificationsAPI } from '../../services/api';
import { useNotifications } from '../notifications/NotificationProvider';
import { AuthContext } from '../../contexts/AuthContext';
import { isCoachInTrainingGroup } from '../../utils/roles';
import {
  BellIcon,
  ClockIcon,
  MegaphoneIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const NotificationPanel = ({ trainingGroup }) => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);

  // Check if user can send notifications
  const canSendNotifications = isCoachInTrainingGroup(user, trainingGroup?.id);

  // Custom notification mutation
  const customNotificationMutation = useMutation(
    (data) => notificationsAPI.sendCustomNotification(data),
    {
      onSuccess: () => {
        showSuccess('Custom notification sent successfully');
        setShowCustomModal(false);
      },
      onError: (error) => {
        showError(error.response?.data?.detail || 'Failed to send custom notification');
      },
    }
  );

  // Availability reminder mutation
  const availabilityReminderMutation = useMutation(
    (data) => notificationsAPI.sendAvailabilityReminder(data),
    {
      onSuccess: () => {
        showSuccess('Availability reminder sent successfully');
        setShowAvailabilityModal(false);
      },
      onError: (error) => {
        showError(error.response?.data?.detail || 'Failed to send availability reminder');
      },
    }
  );

  if (!canSendNotifications) {
    return null;
  }

  return (
    <div className="rounded-lg bg-white shadow-md">
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center">
          <BellIcon className="mr-2 h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Communication Center</h3>
        </div>
        <p className="mt-1 text-sm text-gray-600">Send messages to training group members</p>
      </div>

      <div className="space-y-4 p-6">
        {/* Availability Reminder */}
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center">
            <ClockIcon className="mr-3 h-5 w-5 text-blue-500" />
            <div>
              <h4 className="text-sm font-medium text-blue-900">Availability Reminder</h4>
              <p className="text-sm text-blue-700">Remind members to set their availability</p>
            </div>
          </div>
          <button
            onClick={() => setShowAvailabilityModal(true)}
            className="inline-flex items-center rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <PaperAirplaneIcon className="mr-1 h-4 w-4" />
            Send Reminder
          </button>
        </div>

        {/* Custom Message */}
        <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center">
            <MegaphoneIcon className="mr-3 h-5 w-5 text-green-500" />
            <div>
              <h4 className="text-sm font-medium text-green-900">Custom Message</h4>
              <p className="text-sm text-green-700">Send a custom message to group members</p>
            </div>
          </div>
          <button
            onClick={() => setShowCustomModal(true)}
            className="inline-flex items-center rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <PaperAirplaneIcon className="mr-1 h-4 w-4" />
            Send Message
          </button>
        </div>
      </div>

      {/* Custom Notification Modal */}
      <CustomNotificationModal
        isOpen={showCustomModal}
        onClose={() => setShowCustomModal(false)}
        onSend={(data) =>
          customNotificationMutation.mutate({
            ...data,
            training_group_id: trainingGroup.id,
          })
        }
        isLoading={customNotificationMutation.isLoading}
        trainingGroup={trainingGroup}
      />

      {/* Availability Reminder Modal */}
      <AvailabilityReminderModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        onSend={(data) =>
          availabilityReminderMutation.mutate({
            ...data,
            training_group_id: trainingGroup.id,
          })
        }
        isLoading={availabilityReminderMutation.isLoading}
        trainingGroup={trainingGroup}
      />
    </div>
  );
};

// Custom Notification Modal Component
const CustomNotificationModal = ({ isOpen, onClose, onSend, isLoading, trainingGroup }) => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    priority: 'normal',
    action_url: '',
    send_to_all: true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSend(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative top-20 mx-auto w-full max-w-lg rounded-md border bg-white p-5 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <MegaphoneIcon className="mr-2 h-6 w-6 text-indigo-500" />
            <h3 className="text-lg font-medium text-gray-900">Send Training Group Message</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Explanation Section */}
        <div className="mb-6 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
          <div className="flex items-start">
            <InformationCircleIcon className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-500" />
            <div>
              <h4 className="mb-1 text-sm font-medium text-indigo-900">
                Training Group Communication
              </h4>
              <p className="text-sm text-indigo-700">
                This message will be sent only to members of the{' '}
                <strong>{trainingGroup?.name}</strong> training group. Use this for group-specific
                announcements, schedule updates, or coordination messages that are relevant to this
                particular group.
              </p>
              <div className="mt-2 text-xs text-indigo-600">
                • Recipients: {trainingGroup?.user_memberships?.length || 0} group members
                <br />
                • Visibility: Training group only
                <br />• Purpose: Group coordination and updates
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Message Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., Training Schedule Update, Equipment Reminder"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              A clear, concise title that summarizes your message
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Message Content</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Write your message to the training group members..."
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Provide detailed information for your group members
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Priority Level</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="low">Low - General information</option>
              <option value="normal">Normal - Standard updates</option>
              <option value="high">High - Important notices</option>
              <option value="urgent">Urgent - Immediate attention required</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Higher priority messages will be more prominently displayed
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Action Link (optional)
            </label>
            <input
              type="text"
              value={formData.action_url}
              onChange={(e) => setFormData({ ...formData, action_url: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="/availability, /planning, /training-sessions"
            />
            <p className="mt-1 text-xs text-gray-500">
              Add a link to direct members to a specific page (e.g., availability form, schedule)
            </p>
          </div>

          <div className="flex items-start space-x-2">
            <input
              type="checkbox"
              id="send_to_all"
              checked={formData.send_to_all}
              onChange={(e) => setFormData({ ...formData, send_to_all: e.target.checked })}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <label htmlFor="send_to_all" className="text-sm font-medium text-gray-700">
                Send to all group members
              </label>
              <p className="text-xs text-gray-500">
                Uncheck to send only to active members (recommended for training-specific messages)
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isLoading ? 'Sending Message...' : 'Send to Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Availability Reminder Modal Component
const AvailabilityReminderModal = ({ isOpen, onClose, onSend, isLoading, trainingGroup }) => {
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    send_to_all: false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSend({
      ...formData,
      start_date: new Date(formData.start_date).toISOString(),
      end_date: new Date(formData.end_date).toISOString(),
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative top-20 mx-auto w-full max-w-lg rounded-md border bg-white p-5 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <ClockIcon className="mr-2 h-6 w-6 text-blue-500" />
            <h3 className="text-lg font-medium text-gray-900">Send Availability Reminder</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Explanation Section */}
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start">
            <InformationCircleIcon className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
            <div>
              <h4 className="mb-1 text-sm font-medium text-blue-900">
                Availability Reminder for {trainingGroup?.name}
              </h4>
              <p className="text-sm text-blue-700">
                This reminder will prompt group members to update their availability for the
                specified date range. By default, it only sends to members who haven't set their
                availability yet, helping ensure complete participation data for training session
                planning.
              </p>
              <div className="mt-2 text-xs text-blue-600">
                • Purpose: Collect member availability data • Recipients: Members missing
                availability (or all if selected) • Includes: Direct link to availability form
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Availability Period - Start Date
            </label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              First date for which you need availability information
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Availability Period - End Date
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Last date for which you need availability information
            </p>
          </div>

          <div className="flex items-start space-x-2">
            <input
              type="checkbox"
              id="send_to_all_availability"
              checked={formData.send_to_all}
              onChange={(e) => setFormData({ ...formData, send_to_all: e.target.checked })}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <label
                htmlFor="send_to_all_availability"
                className="text-sm font-medium text-gray-700"
              >
                Send to all members
              </label>
              <p className="text-xs text-gray-500">
                By default, reminders are only sent to members who haven't set their availability.
                Check this to send to everyone, including those who already have availability set.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? 'Sending Reminder...' : 'Send Availability Reminder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NotificationPanel;
